import {
  ChartBar,
  Envelope,
  FileText,
  Globe,
  Image as ImageIcon,
  Key,
  Link as LinkIcon,
  MapPin,
  type Icon as PhosphorIcon,
} from '@phosphor-icons/react';
import type { DeliverableIcon, DeliverableKind, StaffDeliverable, VaultFile } from '@mybusinessmate/schema';

const ICONS: Record<DeliverableIcon, PhosphorIcon> = {
  globe: Globe,
  'map-pin': MapPin,
  envelope: Envelope,
  key: Key,
  image: ImageIcon,
  'file-text': FileText,
  link: LinkIcon,
  'chart-bar': ChartBar,
};

const KIND_ICON: Record<DeliverableKind, PhosphorIcon> = { login: Key, file: FileText, link: LinkIcon };

/** The tile icon for an item: the one the team picked, else one that suits its kind (a picture for an image file). */
export function vaultIcon(item: { icon: DeliverableIcon | null; contentType?: string }, kind: DeliverableKind) {
  if (item.icon) return ICONS[item.icon];
  return kind === 'file' && item.contentType?.startsWith('image/') ? ImageIcon : KIND_ICON[kind];
}

/** "ZIP", "PDF", "PNG": from the file's extension, else its content type. */
export function fileTypeLabel(file: Pick<VaultFile, 'fileName' | 'contentType'>): string {
  const ext = /\.([a-z0-9]{1,5})$/i.exec(file.fileName)?.[1];
  return (ext ?? file.contentType.split('/')[1] ?? 'File').toUpperCase();
}

export function formatBytes(bytes: number): string {
  if (bytes >= 1024 ** 3) return `${Number((bytes / 1024 ** 3).toFixed(1))} GB`;
  if (bytes >= 1024 ** 2) return `${Math.round(bytes / 1024 ** 2)} MB`;
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${bytes} B`;
}

/** The line under a file's name (Figma "ZIP · 12 files · Brand kit"): the team's note, or the size when there is none. */
export function fileMeta(file: VaultFile): string {
  return [fileTypeLabel(file), file.description || formatBytes(file.sizeBytes), file.category]
    .filter(Boolean)
    .join(' · ');
}

/** "0:24": what is left of the countdown on an open login. */
export function countdownText(seconds: number): string {
  const s = Math.max(0, Math.ceil(seconds));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

/** Whether an item matches the search box (title, the line under it, a file's name or group). */
export function matchesSearch(
  item: { title: string; description: string | null; category?: string | null; fileName?: string },
  query: string,
): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return [item.title, item.description, item.category, item.fileName].some((v) => v?.toLowerCase().includes(q));
}

/** Storage as "1.4 GB of 2 GB", and how full the bar is (0 to 1). */
export function storageText(used: number, limit: number): { text: string; ratio: number } {
  return {
    text: `${formatBytes(used)} of ${formatBytes(limit)}`,
    ratio: limit > 0 ? Math.min(used / limit, 1) : 0,
  };
}

// --- The team's list (Figma A05.11) -----------------------------------------------------------------------------------

const day = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short' });
const dayTime = new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit' });

/** Whether the client can see anything of this item (one field is enough: "2 of 3 fields shared with client"). */
export const sharedWithClient = (item: StaffDeliverable) => item.fields.some((f) => f.visibleToClient);

/** The names of what a login holds, lower-cased, without the field already shown as the address. */
const names = (item: StaffDeliverable, shown: StaffDeliverable['fields'][number] | undefined) =>
  item.fields
    .filter((f) => f !== shown)
    .map((f) => f.label.toLowerCase())
    .join(', ') || null;

/**
 * The grey line under an item's name (Figma A05.11): where it is and what it holds, then who added it and when.
 * A login names its fields ("username, password"), a file its type and size, and an item shared only in part says so.
 */
export function staffMeta(item: StaffDeliverable): string {
  const first = item.fields.find((f) => f.value && !f.secret && f.type !== 'file');
  const host = first?.value?.replace(/^https?:\/\//i, '').replace(/\/$/, '');
  const shared = item.fields.filter((f) => f.visibleToClient).length;
  const parts: (string | null)[] = [];
  if (item.kind === 'login') {
    parts.push(host ?? null);
    parts.push(item.fields.length > 0 && shared === item.fields.length ? names(item, first) : null);
    if (shared > 0 && shared < item.fields.length)
      parts.push(`${shared} of ${item.fields.length} fields shared with client`);
    if (shared === 0) parts.push(names(item, first), 'team only');
  } else if (item.kind === 'file') {
    const file = item.fields.find((f) => f.file)?.file;
    parts.push(file ? (/\.([a-z0-9]{1,5})$/i.exec(file.name)?.[1] ?? '').toUpperCase() || null : null);
    parts.push(file ? formatBytes(file.size) : null);
    parts.push(item.description, item.category);
  } else {
    parts.push(host ?? null, item.description);
  }
  if (item.kind === 'login' && item.description) parts.splice(1, 0, item.description);
  parts.push(item.createdByName ? `added by ${item.createdByName}` : null, day.format(new Date(item.createdAt)));
  return parts.filter(Boolean).join(' · ');
}

/** "today, 4:12 PM", or "21 Oct, 11:03 AM": when a login was opened. */
export function revealedWhen(iso: string, now: Date = new Date()): string {
  const at = new Date(iso);
  const same = at.toDateString() === now.toDateString();
  return `${same ? 'today' : day.format(at)}, ${dayTime.format(at)}`;
}

/** What an item matches in the team's search box: its name, the line under it, and its project. */
export function matchesStaffSearch(item: StaffDeliverable, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return [item.title, item.description, item.category, item.projectName, ...item.fields.map((f) => f.label)].some((v) =>
    v?.toLowerCase().includes(q),
  );
}
